import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Icon,
} from "@mui/material";
import {
  IDraftJournalEntryRequest,
  IListCoaRequest,
  ICoaAccount,
  IChartOfAccount,
} from "../../../accounting.interface";
import { flattenAccounts } from "../../../utils";
import { useJournals } from "../hooks/useJournals";
import { useBranch } from "../../../components/BranchContext";
import { useAuth } from "../../../../auth/hooks/useAuth";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { accountingService } from "../../../../../services/accounting";
import { toast } from "react-toastify";
import {
  UI_CONFIG,
  FormData,
  JournalLineForm,
  calculateJournalTotals,
  createInitialFormData,
  transformFormDataToJournalEntry,
  updateJournalLine,
  updateJournalLineWithAccount,
  addJournalLine,
  removeJournalLine,
  handleAmountChange,
  getFormValidationState,
} from "../utils";
import {
  JournalHeaderSection,
  JournalLinesSection,
  TotalsValidationSection,
} from "./JournalEntryFormSections";
import { PostConfirmationDialog } from "./PostConfirmationDialog";

interface JournalEntryFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (journalEntry: Partial<IDraftJournalEntryRequest>) => Promise<void>;
  isLoading?: boolean;
}

// Utility function to convert API response to UI format (copied from chart.of.accounts.tsx)
const convertApiToUiFormat = (
  apiAccounts: ICoaAccount[]
): IChartOfAccount[] => {
  const convertedAccounts: IChartOfAccount[] = apiAccounts.map((account) => ({
    id: account.id,
    accountCode: account.account_code,
    accountName: account.account_name,
    accountType: account.account_type,
    currency: account.currency,
    openingBalance: account.opening_balance,
    currentBalance: account.current_balance,
    parentAccountId: account.parent_account_id || undefined,
    isParent: account.is_parent,
    allowDirectPosting: account.allow_direct_posting,
    level: account.level,
    isActive: account.is_active,
    createdAt: new Date(account.created_at).toISOString(),
    updatedAt: new Date(account.updated_at).toISOString(),
    children: [],
  }));

  return buildAccountHierarchy(convertedAccounts);
};

// Function to build hierarchical structure from flat array (copied from chart.of.accounts.tsx)
const buildAccountHierarchy = (
  accounts: IChartOfAccount[]
): IChartOfAccount[] => {
  const accountMap = new Map<string, IChartOfAccount>();
  const rootAccounts: IChartOfAccount[] = [];

  // Create map for quick lookup
  accounts.forEach((account) => {
    accountMap.set(account.id, { ...account, children: [] });
  });

  // Build hierarchy
  accounts.forEach((account) => {
    const currentAccount = accountMap.get(account.id);
    if (!currentAccount) return;

    if (account.parentAccountId) {
      const parent = accountMap.get(account.parentAccountId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(currentAccount);
      }
    } else {
      rootAccounts.push(currentAccount);
    }
  });

  return rootAccounts;
};

/**
 * Professional Manual Journal Entry Form Component
 *
 * Features:
 * - Clean separation of concerns with utility functions
 * - Modular section components for maintainability
 * - Comprehensive validation with real-time feedback
 * - Performance optimized with memoization
 * - Professional error handling and user experience
 */
export const JournalEntryForm: React.FC<JournalEntryFormProps> = ({
  open,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  // Form state management
  const [formData, setFormData] = useState<FormData>(createInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lineErrors, setLineErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const [showPostConfirmation, setShowPostConfirmation] = useState(false);

  // Accounts state management
  const [accounts, setAccounts] = useState<IChartOfAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  // Fetch accounts data
  const fetchAccounts = async () => {
    // Don't fetch if no branch is selected or organization level is selected
    if (!selectedBranch || selectedBranch === "organization") {
      setAccounts([]);
      return;
    }

    setAccountsLoading(true);
    try {
      const requestData: IListCoaRequest = {
        pageIndex: 0,
        pageSize: 1000, // Get all accounts
        sortField: "account_code",
        sortOrder: "asc",
        selected_branch_id: selectedBranch,
      };

      const response = await accountingService.listCoa(requestData);
      const convertedAccounts = convertApiToUiFormat(response.data);
      setAccounts(convertedAccounts);
    } catch (error: any) {
      console.error("Failed to fetch accounts for journal entry:", error);
      toast.error("Failed to load accounts. Please try again.");
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  // Sync branch_id with selectedBranch from context
  useEffect(() => {
    if (open && selectedBranch) {
      setFormData((prev) => ({
        ...prev,
        branch_id: selectedBranch,
      }));
    }
  }, [selectedBranch, open]);

  // Fetch accounts when component mounts or branch changes
  useEffect(() => {
    if (open) {
      fetchAccounts();
    }
  }, [selectedBranch, open]);

  // Memoized computed values for performance
  const postableAccounts = useMemo(() => {
    const allAccounts = flattenAccounts(accounts);
    return allAccounts.filter(
      (account: IChartOfAccount) =>
        account.allowDirectPosting && account.isActive !== false
    );
  }, [accounts]);

  const totals = useMemo(
    () => calculateJournalTotals(formData.journal_lines),
    [formData.journal_lines]
  );

  // Professional validation state for button enablement
  const validationState = useMemo(
    () => getFormValidationState(formData, totals),
    [formData, totals]
  );

  // Validation function - optimized with useCallback
  //   const validateForm = useCallback((): boolean => {
  //     const validation = validateJournalEntryForm(formData, totals);

  //     setErrors({ ...validation.headerErrors, ...validation.balanceErrors });
  //     setLineErrors(validation.lineErrors);

  //     return validation.isValid;
  //   }, [formData, totals]);

  // Event handlers - all optimized and using utility functions
  const handleFieldChange = useCallback(
    (field: keyof FormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [errors]
  );

  const handleLineChange = useCallback(
    (lineId: string, field: keyof JournalLineForm, value: any) => {
      setFormData((prev) => ({
        ...prev,
        journal_lines: updateJournalLine(
          prev.journal_lines,
          lineId,
          field,
          value
        ),
      }));

      // Clear line errors
      if (lineErrors[lineId]?.[field]) {
        setLineErrors((prev) => ({
          ...prev,
          [lineId]: { ...prev[lineId], [field]: "" },
        }));
      }
    },
    [lineErrors]
  );

  const handleAccountSelect = useCallback(
    (lineId: string, accountId: string) => {
      const account = postableAccounts.find((acc: any) => acc.id === accountId);
      if (account) {
        setFormData((prev) => ({
          ...prev,
          journal_lines: updateJournalLineWithAccount(
            prev.journal_lines,
            lineId,
            {
              id: account.id,
              accountCode: account.accountCode,
              accountName: account.accountName,
            }
          ),
        }));
      }
    },
    [postableAccounts]
  );

  const handleAmountChangeWrapper = useCallback(
    (lineId: string, field: "debit" | "credit", value: number | null) => {
      setFormData((prev) => ({
        ...prev,
        journal_lines: handleAmountChange(
          prev.journal_lines,
          lineId,
          field,
          value
        ),
      }));
    },
    []
  );

  const handleAddLine = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      journal_lines: addJournalLine(prev.journal_lines),
    }));
  }, []);

  const handleRemoveLine = useCallback((lineId: string) => {
    setFormData((prev) => ({
      ...prev,
      journal_lines: removeJournalLine(prev.journal_lines, lineId),
    }));
  }, []);

  // Save handlers - professional validation with early returns
  const handleSaveAsDraft = useCallback(async () => {
    if (!validationState.canSaveAsDraft) return;

    // TODO: Update transformFormDataToJournalEntry to return API format
    const journalEntry = transformFormDataToJournalEntry(formData, "Draft");
    await onSave(journalEntry as any);
  }, [formData, validationState.canSaveAsDraft, onSave]);

  const handlePostEntry = useCallback(async () => {
    if (!validationState.canPost) return;

    // TODO: Update transformFormDataToJournalEntry to return API format
    const journalEntry = transformFormDataToJournalEntry(formData, "Posted");
    await onSave(journalEntry as any);
    setShowPostConfirmation(false);
  }, [formData, validationState.canPost, onSave]);

  const handleReset = useCallback(() => {
    setFormData(createInitialFormData());
    setErrors({});
    setLineErrors({});
    setShowPostConfirmation(false);
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        keepMounted={false}
        disablePortal={false}
        PaperProps={{
          sx: {
            maxHeight: UI_CONFIG.DIALOG_MAX_HEIGHT,
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Manual Journal Entry
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: "error.main",
                },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedBranch === "organization" ? (
            // Organization Level Placeholder
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                textAlign: "center",
                border: `2px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
              }}
            >
              <Icon
                sx={{
                  fontSize: 64,
                  color: "warning.main",
                  mb: 2,
                }}
              >
                business
              </Icon>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "warning.main",
                  mb: 1,
                }}
              >
                Choose a Specific Branch
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                  maxWidth: 400,
                }}
              >
                Journal entries must be created for a specific branch. Please
                select a branch from the header branch selector to continue.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Icon sx={{ color: "info.main", fontSize: 20 }}>info</Icon>
                <Typography variant="body2" sx={{ color: "info.main" }}>
                  Use the branch selector in the top header to choose a branch
                </Typography>
              </Box>
            </Box>
          ) : (
            // Normal Form Content
            <>
              {/* Section 1: Journal Header */}
              <JournalHeaderSection
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldChange}
              />

              {/* Section 2: Journal Lines */}
              <JournalLinesSection
                formData={formData}
                lineErrors={lineErrors}
                postableAccounts={postableAccounts}
                onLineChange={handleLineChange}
                onAccountSelect={handleAccountSelect}
                onAmountChange={handleAmountChangeWrapper}
                onAddLine={handleAddLine}
                onRemoveLine={handleRemoveLine}
              />

              {/* Section 3: Totals and Validation */}
              <TotalsValidationSection
                formData={formData}
                totals={totals}
                validationState={validationState}
                errors={errors}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleReset}
            disabled={isLoading || selectedBranch === "organization"}
          >
            Reset Form
          </Button>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveAsDraft}
            disabled={
              isLoading ||
              selectedBranch === "organization" ||
              !validationState.canSaveAsDraft ||
              !hasPermission(
                "accounting.journals.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            startIcon={<Icon>save</Icon>}
            title={
              selectedBranch === "organization"
                ? "Please select a specific branch first"
                : !validationState.canSaveAsDraft
                ? "Please fill required fields and add at least one valid journal line"
                : "Save as draft for later completion"
            }
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowPostConfirmation(true)}
            disabled={
              isLoading ||
              selectedBranch === "organization" ||
              !validationState.canPost ||
              !hasPermission(
                "accounting.journals.post",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            startIcon={<Icon>check_circle</Icon>}
            title={
              selectedBranch === "organization"
                ? "Please select a specific branch first"
                : !validationState.canPost
                ? validationState.canSaveAsDraft
                  ? "Entry must be balanced to post"
                  : "Please complete all required fields and balance the entry"
                : "Post entry to the ledger"
            }
          >
            Post Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post Confirmation Dialog */}
      <PostConfirmationDialog
        open={showPostConfirmation}
        onClose={() => setShowPostConfirmation(false)}
        onConfirm={handlePostEntry}
        formData={formData}
        totals={totals}
        validationState={validationState}
        isLoading={isLoading}
      />
    </>
  );
};
